import { fileTypeFromBuffer } from 'file-type';
import { z } from 'zod';

const CACHE_NAME = 'IMAGES_V1';

const RequestPostMessageEvent = z.object({
  data: z.string().url(),
});

type RequestPostMessageEvent = (typeof RequestPostMessageEvent)['_output'];

/*
 * This worker powers WebWorkerImage component.
 * It is responsible for loading images from network and caching them.
 * */

// Source worker: https://blog.logrocket.com/optimized-media-loading-web-workers/
// Source cache: https://developer.mozilla.org/en-US/docs/Web/API/Service_Worker_API/Using_Service_Workers

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

const putInCache = async ({ url, response }: { url: string; response: Response }) => {
  // Request scheme 'blob' is unsupported: https://github.com/w3c/webextensions/issues/72
  // WebWorkerImage does not need to cache blob responses: the next time the user visits the page,
  // we're fetching the image from the cdn using an uploadKey. We're avoiding caching blob responses to avoid errors,
  // but we won't use the cache for blobs anyway.
  if (url.includes('blob:')) return;

  const cache = await caches.open(CACHE_NAME);
  await cache.put(url, response);
};

const cacheFirst = async ({ url }: { url: string }): Promise<Response> => {
  // First try to get the resource from the cache
  const responseFromCache = await caches.match(url);
  if (responseFromCache) {
    return responseFromCache;
  }

  // Next try to get the resource from the network
  try {
    const responseFromNetwork = await fetch(url);

    // response may be used only once
    // we need to save clone to put one copy in cache
    // and serve second one
    putInCache({ url, response: responseFromNetwork.clone() });
    return responseFromNetwork;
  } catch (error) {
    // We must always return a Response object
    return new Response('Network error happened', {
      status: 408,
      headers: { 'Content-Type': 'text/plain' },
    });
  }
};

addEventListener('message', async (e: RequestPostMessageEvent) => {
  const parsedEvent = RequestPostMessageEvent.safeParse(e);

  if (!parsedEvent.success) {
    // eslint-disable-next-line no-console
    console.error(parsedEvent.error);
    return;
  }

  const url = parsedEvent.data.data;
  const response = await cacheFirst({ url });
  const fileBlob = await response.blob();
  const fileType = await fileTypeFromBuffer(await fileBlob?.arrayBuffer());

  const image =
    fileType?.mime && ACCEPTED_IMAGE_TYPES.includes(fileType.mime)
      ? URL.createObjectURL(fileBlob)
      : null;
  postMessage(image);
});
